import { Component, computed, Inject, signal } from '@angular/core'
import { FieldTree, form, minLength, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'

import { AsiakkaanTuote, LaskunAlv, LaskunTyypit, VerottomanMyynninPeruste } from '@lemon/core-firebase/model/lasku'
import { LASKUN_OLETUSYKSIKKO } from '@lemon/core-firebase/model/laskun-yksikot'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { Valuutta } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { firstValueFrom, Observable } from 'rxjs'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { AlvService } from '../../_jaettu-angular/service/alv.service'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { KonfiguraatioPalvelu } from '../../_jaettu-angular/service/lasku/configuration.service'
import { LaskuttavaAsiakasProvider } from '../../_jaettu-angular/service/lasku/laskuttava-asiakas.service'
import { TuotePoistoVarmistusDialog, TuotePoistoVarmistusDialogData } from './tuote-poisto-varmistus.dialog'
import { annaVerollinenHinta, onkoAlvVoimassa } from './tuoterekisteri-logiikka'

export interface TuoteDialogData {
  tuote: AsiakkaanTuote | null
}

// Valinta näyttää null-arvoisen vaihtoehdon tyhjänä, joten valuutattoman tuotteen vaihtoehdolla on oma arvonsa
const EI_VALUUTTAA = ''

interface TuoteLomake {
  nimi: string
  hinta: number | null
  valuutta: string
  yksikko: string
  alv: LaskunAlv | null
  verottomuudenPeruste: VerottomanMyynninPeruste | null
}

@Component({
  templateUrl: './tuote.dialog.html',
  styleUrls: ['./tuote.dialog.css'],
  standalone: false,
})
export class TuoteDialog {
  readonly lomake: FieldTree<TuoteLomake>
  readonly verollinenHinta = computed(() => annaVerollinenHinta(this.lomake.hinta().value(), this.lomake.alv().value()?.prosentti ?? null, this._currencyService))
  valuutatObservable: Observable<Valuutta[]>
  readonly alvt = signal<LaskunAlv[]>([])
  onkoValuutaton: boolean
  verottomanMyynninPerusteet = Object.values(VerottomanMyynninPeruste)
  readonly eiValuuttaa = EI_VALUUTTAA

  private readonly _verottomuudenPerusteTarpeen = signal(false)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TuoteDialogData,
    private _dialogRef: MatDialogRef<TuoteDialog, AsiakkaanTuote | 'poistettu'>,
    private _dialog: MatDialog,
    private _validationService: FormValidationService,
    private _konfiguraatioPalvelu: KonfiguraatioPalvelu,
    private _alvService: AlvService,
    private _laskuKopioija: LaskuKopioija,
    private _currencyService: CurrencyService,
    private _dateService: DateService,
    private _laskuSharedService: LaskuSharedService,
    private _laskuService: LaskuService,
    private _laskuttavaAsiakasProvider: LaskuttavaAsiakasProvider,
  ) {
    const tuote = data.tuote
    this.valuutatObservable = this._konfiguraatioPalvelu.valuutatObservable
    this.onkoValuutaton = !!tuote && !tuote.valuutta

    const yksikko = tuote?.yksikko ?? LASKUN_OLETUSYKSIKKO

    const alkuarvot: TuoteLomake = {
      nimi: tuote?.nimi ?? '',
      hinta: tuote?.hinta ?? null,
      valuutta: tuote ? (tuote.valuutta ?? EI_VALUUTTAA) : 'EUR',
      yksikko,
      alv: null,
      verottomuudenPeruste: tuote?.verottomuudenPeruste ?? null,
    }
    this.lomake = form(signal(alkuarvot), polku => {
      required(polku.nimi)
      minLength(polku.nimi, 2)
      required(polku.hinta)
      required(polku.yksikko)
      required(polku.alv)
      required(polku.verottomuudenPeruste, { when: () => this._verottomuudenPerusteTarpeen() })
    })

    arvonMuutokset(this.lomake.alv).subscribe(alv => this._paivitaVerottomuudenPeruste(alv))

    this._asetaAlvt(tuote)
  }

  private async _asetaAlvt(tuote: AsiakkaanTuote | null) {
    const kotimaanAlvt = await this._alvService.annaLaskutyypinAlvt(LaskunTyypit.TAVALLINEN, null)
    const laskuttajaAlvRekisterissa = await firstValueFrom(this._laskuttavaAsiakasProvider.onkoAlvRekisterissa())
    const tanaan = this._dateService.currentNumberDate()
    const tuotteenAlvTunniste = tuote?.alv?.[LaskunTyypit.TAVALLINEN.avain]

    if (laskuttajaAlvRekisterissa) {
      this.alvt.set(kotimaanAlvt.filter(alv => onkoAlvVoimassa(alv, tanaan, this._dateService) || alv.tunniste === tuotteenAlvTunniste))
    } else {
      this.alvt.set(kotimaanAlvt.filter(alv => alv.prosentti === 0))
    }

    const tuotteenAlv = this.alvt().find(alv => alv.tunniste === tuotteenAlvTunniste)
    asetaArvo(this.lomake.alv, tuotteenAlv ?? this._alvService.annaDefaultAlv(this.alvt(), tanaan) ?? this.alvt()[0])
  }

  get naytetaankoVerottomuudenPeruste(): boolean {
    return this._laskuSharedService.onkoVerottomuudenPerusteTarpeen(this.lomake.alv().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const naytetaanko = kentta().dirty() || kentta().touched()
    return (
      naytetaanko &&
      kentta()
        .errors()
        .some(virhe => virhe.kind === laji)
    )
  }

  asetaYksikko(yksikko: string) {
    asetaArvo(this.lomake.yksikko, yksikko)
  }

  private async _paivitaVerottomuudenPeruste(alv: LaskunAlv) {
    if (this._laskuSharedService.onkoVerottomuudenPerusteTarpeen(alv)) {
      this._verottomuudenPerusteTarpeen.set(true)
      if (!this.lomake.verottomuudenPeruste().value()) {
        const asetukset = await firstValueFrom(this._laskuService.asetuksetObservable)
        asetaArvo(this.lomake.verottomuudenPeruste, asetukset?.verottomanMyynninOletusperuste ?? null)
      }
    } else {
      this._verottomuudenPerusteTarpeen.set(false)
      asetaArvo(this.lomake.verottomuudenPeruste, null)
    }
  }

  vertaaAlvt(alv1: LaskunAlv, alv2: LaskunAlv): boolean {
    return alv1?.tunniste === alv2?.tunniste
  }

  async poistaTuote() {
    const data: TuotePoistoVarmistusDialogData = { tuote: this.data.tuote }
    const poistettu = await firstValueFrom(this._dialog.open(TuotePoistoVarmistusDialog, { data }).afterClosed())
    if (poistettu) {
      this._dialogRef.close('poistettu')
    }
  }

  tallenna() {
    const virheitaLomakkeella = this._validationService.onkoLomakkeessaVirheita(this.lomake)
    if (virheitaLomakkeella) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    const arvot = this.lomake().value()
    const tallennettava = this.data.tuote ? this._laskuKopioija.copyTuoteMuokattavaksi(this.data.tuote) : this._laskuKopioija.annaUusiTuote()
    tallennettava.nimi = this._validationService.processValue(arvot.nimi)
    tallennettava.hinta = arvot.hinta
    tallennettava.yksikko = arvot.yksikko
    tallennettava.alv = { ...(tallennettava.alv ?? {}), [LaskunTyypit.TAVALLINEN.avain]: arvot.alv.tunniste }
    if (arvot.valuutta) {
      tallennettava.valuutta = arvot.valuutta
    }
    if (arvot.verottomuudenPeruste) {
      tallennettava.verottomuudenPeruste = arvot.verottomuudenPeruste
    } else {
      delete tallennettava.verottomuudenPeruste
    }
    this._dialogRef.close(tallennettava)
  }
}
